import React, { useEffect, useState } from 'react';
import { NavLink, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { LayoutGrid, Package, Heart, Store, Ticket, MessageSquareText, MapPin, Bell, User, ShieldCheck, LifeBuoy, ChevronRight, Gift, Wallet } from 'lucide-react';
import { useApp, referralCodeFor } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { VOUCHERS, fmtRM2, timeAgo } from '../data/mockData';
import { StatusBadge } from '../components/commerce/StatusBadge';
import { ProductCard } from '../components/commerce/ProductCard';
import { StoreCard } from '../components/commerce/StoreCard';
import { EmptyState } from '../components/commerce/EmptyState';
import { NotificationBell } from '../components/commerce/NotificationBell';
import { TopUpForm } from '../components/commerce/TopUpForm';
import { toast } from 'sonner';

const MENU = [
  { to: '/my-kelate', icon: LayoutGrid, label: 'Overview', end: true },
  { to: '/my-kelate/orders', icon: Package, label: 'Orders' },
  { to: '/my-kelate/favourites', icon: Heart, label: 'Favourites' },
  { to: '/my-kelate/following', icon: Store, label: 'Following' },
  { to: '/my-kelate/vouchers', icon: Ticket, label: 'Vouchers' },
  { to: '/my-kelate/notifications', icon: Bell, label: 'Notifications' },
  { to: '/my-kelate/profile', icon: User, label: 'Profile' },
  { to: '/my-kelate/support', icon: LifeBuoy, label: 'Support' },
  { to: '/my-kelate/wallet', icon: Wallet, label: 'Wallet' },
];

const TIMELINE_STEPS = ['Order Placed', 'Merchant Confirmed', 'Preparing', 'Done Preparing', 'Out for Delivery', 'Delivered', 'Completed'];

const STATUS_TO_STEP = {
  PENDING: 'Order Placed',
  ACCEPTED: 'Merchant Confirmed',
  PREPARING: 'Preparing',
  READY_FOR_PICKUP: 'Done Preparing',
  PICKED_UP: 'Out for Delivery',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
};

function statusToSteps(order) {
  const steps = [];
  const timeline = order.statusTimeline || order.timeline || [];
  const byStatus = {};
  timeline.forEach((t) => { byStatus[t.status] = t; });
  TIMELINE_STEPS.forEach((label) => {
    const match = Object.entries(STATUS_TO_STEP).find(([, l]) => l === label);
    if (match && byStatus[match[0]]) steps.push({ label, at: byStatus[match[0]].at });
  });
  return steps;
}
const ORDER_TABS = ['All', 'Preparing', 'Out for Delivery', 'Delivered', 'Completed', 'Cancelled'];

function Overview() {
  const { orders, recentlyViewed, customProducts } = useApp();
  const active = orders.filter((o) => !['COMPLETED', 'CANCELLED'].includes(o.orderStatus));
  const recent = recentlyViewed.map((id) => customProducts.find((p) => p.id === id)).filter(Boolean).slice(0, 4);
  const recommended = [...customProducts].sort((a, b) => b.sold - a.sold).slice(0, 4);
  return (
    <div data-testid="mykelate-overview" className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4"><div className="text-xs text-kelate-soft">Active orders</div><div className="mt-1 text-2xl font-semibold text-kelate-ink">{active.length}</div></div>
        <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4"><div className="text-xs text-kelate-soft">Delivered</div><div className="mt-1 text-2xl font-semibold text-kelate-ink">{orders.filter((o) => ['DELIVERED', 'COMPLETED'].includes(o.orderStatus)).length}</div></div>
        <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4"><div className="text-xs text-kelate-soft">Vouchers</div><div className="mt-1 text-2xl font-semibold text-kelate-ink">{VOUCHERS.length}</div></div>
      </div>
      {active.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-kelate-ink">Order status</h3>
          <div className="space-y-2">
            {active.slice(0, 3).map((o) => <OrderRow key={o.id} order={o} />)}
          </div>
        </section>
      )}
      {recent.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-kelate-ink">Recently Viewed</h3>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{recent.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
      <section>
        <h3 className="mb-2 text-sm font-semibold text-kelate-ink">Recommended for you</h3>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{recommended.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      </section>
    </div>
  );
}

function OrderRow({ order }) {
  return (
    <Link to={`/my-kelate/orders/${order.id}`} data-testid={`order-row-${order.orderNumber}`} className="flex items-center gap-3 rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-3 transition-colors hover:border-kelate-wood">
      <AppImage src={order.items[0]?.image} alt="" className="h-12 w-12 rounded-sm border border-kelate-line bg-white object-contain" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-kelate-ink">#{order.orderNumber}</span>
          <StatusBadge status={order.orderStatus} />
        </div>
        <div className="truncate text-xs text-kelate-soft">{order.merchantName} · {order.items.map((i) => `${i.name} ×${i.qty}`).join(', ')}</div>
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold text-kelate-darkwood">{fmtRM2(order.totalCustomerDue)}</div>
        <div className="text-[10px] uppercase tracking-wide text-kelate-soft">Pay on delivery</div>
      </div>
      <ChevronRight size={15} className="text-kelate-line" />
    </Link>
  );
}

function Orders() {
  const { orders } = useApp();
  const [tab, setTab] = React.useState('All');
  const filtered = orders.filter((o) => {
    if (tab === 'All') return true;
    if (tab === 'Preparing') return ['PENDING', 'ACCEPTED', 'PREPARING', 'READY_FOR_PICKUP'].includes(o.orderStatus);
    if (tab === 'Out for Delivery') return ['PICKED_UP', 'OUT_FOR_DELIVERY'].includes(o.orderStatus);
    if (tab === 'Delivered') return o.orderStatus === 'DELIVERED';
    if (tab === 'Completed') return o.orderStatus === 'COMPLETED';
    if (tab === 'Cancelled') return o.orderStatus === 'CANCELLED';
    return true;
  });
  return (
    <div data-testid="mykelate-orders">
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-kelate-line">
        {ORDER_TABS.map((t) => (
          <button key={t} data-testid={`order-tab-${t.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setTab(t)} className={`whitespace-nowrap px-3 py-2 text-sm ${tab === t ? 'border-b-2 border-kelate-darkwood font-semibold text-kelate-darkwood' : 'text-kelate-soft'}`}>{t}</button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.map((o) => <OrderRow key={o.id} order={o} />)}
        {filtered.length === 0 && <EmptyState icon={Package} title="No orders here" description="Orders in this status will appear here." actionLabel="Start Shopping" actionTo="/marketplace" />}
      </div>
    </div>
  );
}

function OrderDetail() {
  const { id } = useParams();
  const { orders } = useApp();
  const order = orders.find((o) => o.id === id);
  if (!order) return <EmptyState icon={Package} title="Order not found" actionLabel="My Orders" actionTo="/my-kelate/orders" />;
  const timelineSteps = statusToSteps(order);
  const currentStep = timelineSteps[timelineSteps.length - 1]?.label;
  const paymentDue = ['PICKED_UP', 'OUT_FOR_DELIVERY'].includes(order.orderStatus) && order.paymentStatus === 'DUE_ON_DELIVERY';
  return (
    <div data-testid="order-detail" className="max-w-2xl space-y-4">
      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-kelate-ink" data-testid="order-detail-number">Order #{order.orderNumber}</h2>
            <div className="text-xs text-kelate-soft">{order.merchantName} · {timeAgo(order.createdAt)}</div>
          </div>
          <StatusBadge status={order.orderStatus} testId="order-detail-status" />
        </div>
        {paymentDue && (
          <div className="mt-3 rounded-sm border border-kelate-wood/40 bg-[#F4EDE0] px-3 py-2 text-xs text-kelate-darkwood" data-testid="payment-due-banner">
            Payment due on delivery: <b>{fmtRM2(order.totalCustomerDue)}</b> ({order.paymentMethod === 'TNG_QR' ? 'TNG QR' : order.paymentMethod === 'BANK_TRANSFER' ? 'Bank Transfer' : 'Cash'})
          </div>
        )}
        <div className="mt-4 space-y-2">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex items-center gap-3 text-sm">
              <AppImage src={i.image} alt="" className="h-10 w-10 rounded-sm border border-kelate-line bg-white object-contain" />
              <span className="flex-1 text-kelate-ink">{i.name} {i.variant ? `· ${i.variant}` : ''} ×{i.qty}</span>
              <span className="font-medium text-kelate-darkwood">{fmtRM2(i.price * i.qty)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-1 border-t border-kelate-line pt-3 text-sm">
          <div className="flex justify-between text-kelate-soft"><span>Items</span><span>{fmtRM2(order.itemSubtotal)}</span></div>
          {order.merchantDiscount > 0 && <div className="flex justify-between text-kelate-sage"><span>Store voucher</span><span>-{fmtRM2(order.merchantDiscount)}</span></div>}
          {order.platformDiscount > 0 && <div className="flex justify-between text-kelate-sage"><span>KELATE voucher</span><span>-{fmtRM2(order.platformDiscount)}</span></div>}
          <div className="flex justify-between text-kelate-soft"><span>Delivery ({order.zone})</span><span>{fmtRM2(order.deliveryFee)}</span></div>
          <div className="flex justify-between border-t border-kelate-line pt-1.5 font-semibold text-kelate-darkwood"><span>Total</span><span data-testid="order-detail-total">{fmtRM2(order.totalCustomerDue)}</span></div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-kelate-soft">
          <MapPin size={12} /> {order.address.line1}, {order.address.district}, {order.address.state}
        </div>
      </div>

      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="order-timeline">
        <h3 className="text-sm font-semibold text-kelate-ink">Order Status</h3>
        <div className="mt-4 space-y-0">
          {TIMELINE_STEPS.map((step, idx) => {
            const done = timelineSteps.some((t) => t.label === step);
            const entry = timelineSteps.find((t) => t.label === step);
            const isCurrent = step === currentStep;
            return (
              <div key={step} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className={`mt-1 h-2.5 w-2.5 rounded-full ${done ? (isCurrent ? 'bg-kelate-darkwood ring-2 ring-kelate-darkwood/20' : 'bg-kelate-sage') : 'bg-kelate-line'}`} />
                  {idx < TIMELINE_STEPS.length - 1 && <span className={`w-px flex-1 ${done ? 'bg-kelate-sage/50' : 'bg-kelate-line'}`} />}
                </div>
                <div className="pb-4">
                  <div className={`text-sm ${done ? (isCurrent ? 'font-semibold text-kelate-darkwood' : 'font-medium text-kelate-ink') : 'text-kelate-soft'}`}>{step}</div>
                  {entry && <div className="text-[11px] text-kelate-soft">{timeAgo(entry.at)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Favourites() {
  const { favourites, customProducts } = useApp();
  const items = favourites.map((id) => customProducts.find((p) => p.id === id)).filter(Boolean);
  return (
    <div data-testid="mykelate-favourites">
      {items.length === 0 ? <EmptyState icon={Heart} title="No favourites yet" description="Tap the heart on any product to save it here." actionLabel="Browse Marketplace" actionTo="/marketplace" /> : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{items.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      )}
    </div>
  );
}

function Following() {
  const { follows, customMerchants } = useApp();
  const items = follows.map((id) => customMerchants.find((m) => m.id === id)).filter(Boolean);
  return (
    <div data-testid="mykelate-following">
      {items.length === 0 ? <EmptyState icon={Store} title="Not following any store" description="Follow stores to hear about promotions first." actionLabel="Find Stores" actionTo="/marketplace" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{items.map((m) => <StoreCard key={m.id} merchant={m} />)}</div>
      )}
    </div>
  );
}

function VouchersPage() {
  return (
    <div className="space-y-3" data-testid="mykelate-vouchers">
      {VOUCHERS.map((v) => (
        <div key={v.code} className="flex items-center gap-3 rounded-2xl border border-dashed border-kelate-wood bg-kelate-surface p-4 shadow-[0_1px_3px_rgba(81,68,55,0.06)]">
          <Ticket size={20} className="shrink-0 text-kelate-wood" />
          <div className="flex-1">
            <div className="text-sm font-bold text-kelate-darkwood">{v.code}</div>
            <div className="text-xs text-kelate-soft">{v.description}</div>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-kelate-sage">{v.type === 'PLATFORM_FUNDED' ? 'Platform' : 'Merchant'}</span>
        </div>
      ))}
    </div>
  );
}

function Notifications() {
  const { notifications, markRead } = useApp();
  const items = notifications.filter((n) => n.recipientType === 'consumer');
  return (
    <div className="space-y-2" data-testid="mykelate-notifications">
      {items.length === 0 && <EmptyState icon={Bell} title="No updates yet" description="Order and delivery updates will appear here." />}
      {items.map((n) => (
        <button key={n.id} onClick={() => markRead(n.id)} data-testid={`consumer-notif-${n.id}`} className={`w-full rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-3 text-left ${n.isRead ? 'opacity-60' : ''}`}>
          <div className="text-sm font-medium text-kelate-ink">{n.title}</div>
          <div className="text-xs text-kelate-soft">{n.message}</div>
          <div className="mt-1 text-[10px] uppercase tracking-wide text-kelate-soft">{timeAgo(n.createdAt)}</div>
        </button>
      ))}
    </div>
  );
}

function Profile() {
  const { user } = useAuth();
  return (
    <div className="max-w-md rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="mykelate-profile">
      <h3 className="text-sm font-semibold text-kelate-ink">Profile</h3>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-kelate-soft">Name</span><span className="text-kelate-ink" data-testid="profile-name">{user?.name}</span></div>
        <div className="flex justify-between"><span className="text-kelate-soft">Email</span><span className="text-kelate-ink">{user?.email}</span></div>
        <div className="flex justify-between"><span className="text-kelate-soft">Phone</span><span className="text-kelate-ink">{user?.phone || '-'}</span></div>
      </div>
      <ReferralCard />
      {user?.role === 'consumer' && (
        <Link to="/merchant/register" data-testid="become-merchant-btn" className="mt-4 flex items-center justify-between rounded-2xl bg-kelate-darkwood px-4 py-3 text-sm font-medium text-kelate-surface transition-colors hover:bg-kelate-wood">
          <span className="flex items-center gap-2"><Store size={15} /> Become a KELATE Merchant</span>
          <ChevronRight size={15} />
        </Link>
      )}
      <div className="mt-4 flex items-center gap-2 border-t border-kelate-line pt-4 text-xs text-kelate-soft">
        <ShieldCheck size={13} className="text-kelate-sage" /> Account secured with password. Addresses are saved at checkout.
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-kelate-soft">
        <LifeBuoy size={13} className="text-kelate-wood" /> Need help? Contact KELATE support at support@kelate.my
      </div>
    </div>
  );
}

export function Support() {
  const { supportTickets, createTicket, replyTicket } = useApp();
  const { user } = useAuth();
  const mine = supportTickets.filter((t) => t.email === user?.email);
  const [form, setForm] = useState({ subject: '', category: 'Order', message: '' });
  const [replyText, setReplyText] = useState({});
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const submit = () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    createTicket({ ...form, name: user.name, email: user.email });
    setForm({ subject: '', category: 'Order', message: '' });
  };
  const statusStyle = { OPEN: 'bg-kelate-red/10 text-kelate-red', IN_PROGRESS: 'bg-kelate-wood/10 text-kelate-wood', RESOLVED: 'bg-kelate-sage/15 text-kelate-sage' };
  return (
    <div className="space-y-6" data-testid="mykelate-support">
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Contact Support</h3>
        <p className="mt-0.5 text-xs text-kelate-soft">Order issues, refunds, delivery problems — we reply here.</p>
        <div className="mt-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="ticket-subject" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={input} />
            <select data-testid="ticket-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input}>
              {['Order', 'Delivery', 'Payment', 'Refund', 'Account', 'Other'].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <textarea data-testid="ticket-message" rows={3} placeholder="Describe your issue..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood" />
          <button data-testid="ticket-submit" onClick={submit} className="rounded-xl bg-kelate-darkwood px-5 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Submit Ticket</button>
        </div>
      </section>
      <section className="space-y-3">
        {mine.length === 0 && <EmptyState icon={LifeBuoy} title="No tickets yet" description="Your support tickets will appear here." />}
        {mine.map((t) => (
          <div key={t.id} data-testid={`my-ticket-${t.id}`} className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-kelate-ink">{t.subject}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyle[t.status] || statusStyle.OPEN}`}>{t.status.replace('_', ' ')}</span>
            </div>
            <div className="mt-0.5 text-[11px] text-kelate-soft">{t.category} · {timeAgo(t.createdAt)}</div>
            <div className="mt-3 space-y-2">
              <div className="rounded-2xl bg-secondary/60 px-3 py-2 text-xs text-kelate-ink">{t.message}</div>
              {t.replies.map((r, i) => (
                <div key={i} className={`rounded-2xl px-3 py-2 text-xs ${r.byAdmin ? 'ml-6 bg-kelate-darkwood text-kelate-surface' : 'mr-6 bg-secondary/60 text-kelate-ink'}`}>
                  <span className="mb-0.5 block text-[10px] uppercase tracking-wide opacity-70">{r.byAdmin ? 'KELATE Support' : 'You'} · {timeAgo(r.at)}</span>
                  {r.message}
                </div>
              ))}
            </div>
            {t.status !== 'RESOLVED' && (
              <div className="mt-3 flex gap-2">
                <input data-testid={`my-ticket-reply-${t.id}`} placeholder="Write a reply..." value={replyText[t.id] || ''} onChange={(e) => setReplyText({ ...replyText, [t.id]: e.target.value })} className={`${input} flex-1`} />
                <button data-testid={`my-ticket-send-${t.id}`} onClick={() => { if ((replyText[t.id] || '').trim()) { replyTicket(t.id, user.name, replyText[t.id].trim(), false); setReplyText({ ...replyText, [t.id]: '' }); } }} className="rounded-xl bg-kelate-darkwood px-4 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Send</button>
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}

function WalletPage() {
  const { user } = useAuth();
  const { wallets, walletTransactions, topUpRequests } = useApp();
  const balance = wallets[user?.email] || 0;
  const myTxns = walletTransactions.filter((t) => t.ownerKey === user?.email);
  const myReqs = topUpRequests.filter((r) => r.ownerKey === user?.email);
  const reqStatus = { PENDING: 'bg-kelate-wood/10 text-kelate-wood', APPROVED: 'bg-kelate-sage/15 text-kelate-sage', REJECTED: 'bg-kelate-red/10 text-kelate-red' };
  return (
    <div className="space-y-5" data-testid="mykelate-wallet">
      <section className="rounded-3xl bg-kelate-darkwood p-6 text-kelate-surface shadow-[0_8px_24px_rgba(81,68,55,0.2)]">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-kelate-wood">KELATE Wallet Balance</div>
        <div className="mt-1 text-4xl font-bold" data-testid="wallet-balance">{fmtRM2(balance)}</div>
        <p className="mt-1 text-xs text-[#CBBFA9]">Use your balance at checkout — choose "KELATE Wallet" and pay instantly, nothing due on delivery.</p>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Top Up Wallet</h3>
        <p className="mt-0.5 mb-3 text-xs text-kelate-soft">Transfer to KELATE, submit your reference, and we credit your wallet after verification.</p>
        <TopUpForm ownerType="consumer" ownerKey={user?.email} ownerName={user?.name} />
      </section>
      {myReqs.length > 0 && (
        <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
          <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Top-Up Requests</h3>
          <div className="space-y-2">
            {myReqs.map((r) => (
              <div key={r.id} data-testid={`topup-req-${r.id}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-2.5 text-xs">
                <span className="font-semibold text-kelate-ink">{fmtRM2(r.amount)} <span className="font-normal text-kelate-soft">via {r.method === 'TNG' ? 'TNG' : 'Bank'} · ref {r.reference}</span></span>
                <span className="flex items-center gap-2">
                  <span className="text-kelate-soft">{timeAgo(r.createdAt)}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${reqStatus[r.status]}`}>{r.status}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Wallet History</h3>
        {myTxns.length === 0 ? (
          <p className="text-xs text-kelate-soft" data-testid="wallet-empty">No wallet activity yet.</p>
        ) : (
          <div className="space-y-2" data-testid="wallet-transactions">
            {myTxns.map((t) => (
              <div key={t.id} data-testid={`wallet-txn-${t.id}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-2.5 text-xs">
                <div>
                  <div className="font-medium text-kelate-ink">{t.note}</div>
                  <div className="text-[10px] uppercase tracking-wide text-kelate-soft">{t.type.replace(/_/g, ' ')} · {timeAgo(t.at)}</div>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${t.amount >= 0 ? 'text-kelate-sage' : 'text-kelate-red'}`}>{t.amount >= 0 ? '+' : ''}{fmtRM2(t.amount)}</div>
                  <div className="text-[10px] text-kelate-soft">Bal {fmtRM2(t.balanceAfter)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ReferralCard() {
  const { user } = useAuth();
  const { referralUses, referralProgram, registerReferralOwner } = useApp();
  useEffect(() => { if (user?.email) registerReferralOwner(user.email, user.name); }, [user, registerReferralOwner]);
  const code = referralCodeFor(user?.email);
  const uses = referralUses.filter((u) => u.ownerEmail === user?.email);
  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(code);
    toast.success('Referral code copied', { description: code });
  };
  return (
    <div className="mt-4 rounded-2xl border border-kelate-line/70 bg-kelate-bg p-4" data-testid="referral-card">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-kelate-soft"><Gift size={13} className="text-kelate-wood" /> Refer &amp; Earn</div>
      <p className="mt-1 text-xs text-kelate-soft">
        Share your code — friends get {referralProgram.discountType === 'PERCENT' ? `${referralProgram.discountValue}%` : fmtRM2(referralProgram.discountValue)} off at checkout{referralProgram.minSpend > 0 ? ` (min spend ${fmtRM2(referralProgram.minSpend)})` : ''}.
      </p>
      <div className="mt-2.5 flex items-center gap-2">
        <span className="flex-1 rounded-xl border border-dashed border-kelate-wood/50 bg-kelate-surface px-3 py-2 text-center font-mono text-sm font-bold tracking-widest text-kelate-darkwood" data-testid="referral-code">{code}</span>
        <button onClick={copy} data-testid="referral-copy-btn" className="rounded-xl bg-kelate-darkwood px-3.5 py-2 text-xs font-medium text-kelate-surface hover:bg-kelate-wood">Copy</button>
      </div>
      <div className="mt-2 text-[11px] text-kelate-soft" data-testid="referral-uses">{uses.length} friend{uses.length === 1 ? '' : 's'} used your code</div>
    </div>
  );
}

export default function MyKelatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unreadCount } = useApp();
  if (!user) { navigate('/account/login?redirect=/my-kelate'); return null; }
  const updates = unreadCount('consumer');
  return (
    <div data-testid="mykelate-page" className="mx-auto max-w-[1280px] px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-kelate-darkwood sm:text-3xl">My KELATE</h1>
          <p className="text-sm text-kelate-soft">Hi, {user.name}</p>
        </div>
        <NotificationBell recipientType="consumer" fullPageUrl="/my-kelate/notifications" />
      </div>
      <div className="grid gap-8 md:grid-cols-[200px_1fr]">
        <aside className="flex gap-1 overflow-x-auto md:flex-col" data-testid="mykelate-sidebar">
          {MENU.map((m) => (
            <NavLink key={m.to} to={m.to} end={m.end} data-testid={`mykelate-nav-${m.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-2 whitespace-nowrap rounded-sm px-3 py-2 text-sm ${isActive ? 'bg-kelate-darkwood text-kelate-surface' : 'text-kelate-soft hover:bg-secondary hover:text-kelate-ink'}`}>
              <m.icon size={15} /> {m.label}
              {m.label === 'Notifications' && updates > 0 && <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-kelate-red px-1 text-[10px] text-white">{updates}</span>}
            </NavLink>
          ))}
        </aside>
        <div>
          <Routes>
            <Route index element={<Overview />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="favourites" element={<Favourites />} />
            <Route path="following" element={<Following />} />
            <Route path="vouchers" element={<VouchersPage />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="profile" element={<Profile />} />
            <Route path="support" element={<Support />} />
            <Route path="wallet" element={<WalletPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
